import React, { useState } from "react";
import FadeIn from "react-fade-in";
import { EnvData } from "../../src/types";
import { StarterPolicy } from "./types";
import Button from "../Button";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { Policy } from "../Policy";
import { Badge } from "./subcomponents/Badges";
import { useOnboardingPolicy } from "./PolicyPage.utils";
import { workbenchToPolicy } from "../../src/workbenchLogic";
import { Facts } from "./subcomponents/Facts";

const stepOrder = ["start", "policy", "fact"] as const;
type Step = typeof stepOrder[number];

export const PolicyPage: React.FC<{
  selectedEnv: EnvData;
  policy: StarterPolicy;
  onBack: () => void;
  onComplete: () => void;
  logAnalytics?: (message: string) => void;
}> = ({ selectedEnv, policy, onBack, onComplete, logAnalytics }) => {
  const [currentStep, setCurrentStep] = useState<Step>("start");
  const [dataComplete, setDataComplete] = useState(false);
  const { savePolicy, workbenchPolicy } = useOnboardingPolicy(
    selectedEnv,
    policy
  );

  const policyString = workbenchToPolicy(workbenchPolicy, { skipTests: true });
  const shouldShowStep = (stepToCheck: Step) =>
    stepOrder.indexOf(stepToCheck) <= stepOrder.indexOf(currentStep);

  return (
    <div className="mx-auto max-w-[800px]">
      <h2 className="text-2xl mb-5">
        Modeling a <Badge>Policy</Badge> in Polar
      </h2>
      <FadeIn>
        <div className="flex flex-col space-y-4">
          <div>
            Let&apos;s create a policy that says that a{" "}
            <Badge>{policy.actor}</Badge> can have <Badge>roles</Badge> on an{" "}
            <Badge>{policy.container}</Badge>, and those <Badge>roles</Badge>{" "}
            grant permissions on that <Badge>{policy.businessObject}</Badge>.
          </div>
          {shouldShowStep("policy") && (
            <FadeIn>
              <div className="grid grid-cols-[max-content_1fr] gap-8 items-start">
                <div className="w-fit">
                  <Policy policy={policyString} />
                </div>
                <div className="max-w-screen-sm">
                  <div className="text-sm whitespace-pre p-[0.5rem] block">
                    <div>Define an actor type named {policy.actor}</div>
                    <div>&nbsp;</div>
                    <div>
                      Declare the roles that a {policy.actor} can have on a{" "}
                      {policy.container}
                    </div>
                    <div>
                      Declare the permissions that a role will allow on a{" "}
                      {policy.businessObject}.
                    </div>
                    <div>&nbsp;</div>
                    <div>
                      Define rules that encode conditional logic based on roles
                      and permissions.
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          )}
          {shouldShowStep("fact") && (
            <FadeIn>
              <Facts
                selectedEnv={selectedEnv}
                onBack={() => setCurrentStep("policy")}
                onComplete={() => {
                  setDataComplete(true);
                  onComplete();
                }}
                state={policy}
                dataComplete={dataComplete}
                backButton={
                  <Button icon={<AiOutlineArrowLeft />} medium onClick={onBack}>
                    Go back
                  </Button>
                }
              />
            </FadeIn>
          )}
          <div className="flex flex-row justify-between">
            {currentStep !== "fact" && (
              <Button icon={<AiOutlineArrowLeft />} medium onClick={onBack}>
                Go back
              </Button>
            )}
            {currentStep === "start" && (
              <div className="flex flex-row justify-end">
                <Button
                  theme="primary"
                  medium
                  onClick={() => {
                    if (logAnalytics) logAnalytics("Show Policy");
                    setCurrentStep("policy");
                  }}
                >
                  Got it, show me the policy
                </Button>
              </div>
            )}
            {currentStep === "policy" && (
              <div className="flex flex-row justify-end">
                <Button
                  theme="primary"
                  medium
                  onClick={() => {
                    if (logAnalytics) logAnalytics("Create Fact");
                    savePolicy();
                    setCurrentStep("fact");
                  }}
                >
                  Ok, let&apos;s create a Fact
                </Button>
              </div>
            )}
          </div>
        </div>
      </FadeIn>
    </div>
  );
};
